import type { EpsonDrawerConfig } from "./epson-drawer";
import { buildEpsonDrawerEndpoint } from "./epson-drawer";
import {
  buildEpsonTicketXml,
  rgbaToEpsonMonochrome,
  type EpsonRasterPage,
} from "./epson-ticket-protocol";

const receiptWidthDots = 576;

function responseCode(xml: string): string | null {
  return xml.match(/\bcode\s*=\s*["']([^"']*)["']/iu)?.[1] || null;
}

async function renderPdf(bytes: Uint8Array): Promise<EpsonRasterPage[]> {
  const [{ getDocument, GlobalWorkerOptions }, workerModule] = await Promise.all([
    import("pdfjs-dist/legacy/build/pdf.mjs"),
    import("pdfjs-dist/legacy/build/pdf.worker.min.mjs?url"),
  ]);
  GlobalWorkerOptions.workerSrc = workerModule.default;
  const loadingTask = getDocument({ data: bytes.slice(), useSystemFonts: true });
  const documentProxy = await loadingTask.promise;
  const pages: EpsonRasterPage[] = [];
  try {
    for (let pageNumber = 1; pageNumber <= documentProxy.numPages; pageNumber += 1) {
      const page = await documentProxy.getPage(pageNumber);
      const originalViewport = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: receiptWidthDots / originalViewport.width });
      const canvas = window.document.createElement("canvas");
      canvas.width = receiptWidthDots;
      canvas.height = Math.ceil(viewport.height);
      const context = canvas.getContext("2d", { alpha: false });
      if (!context) throw new Error("El navegador no pudo preparar el ticket para impresión.");
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvas, viewport, intent: "print", background: "#ffffff" }).promise;
      pages.push(rgbaToEpsonMonochrome(
        context.getImageData(0, 0, canvas.width, canvas.height).data,
        canvas.width,
        canvas.height,
      ));
      page.cleanup();
    }
  } finally {
    await loadingTask.destroy();
  }
  return pages;
}

export async function printPdfDirectlyToEpson(
  bytes: Uint8Array,
  config: Pick<EpsonDrawerConfig, "printerAddress" | "deviceId">,
): Promise<void> {
  const pages = await renderPdf(bytes);
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 75_000);
  try {
    const response = await fetch(buildEpsonDrawerEndpoint(config, 60_000), {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      cache: "no-store",
      headers: {
        "Content-Type": "text/xml; charset=utf-8",
        "If-Modified-Since": "Thu, 01 Jan 1970 00:00:00 GMT",
        SOAPAction: '""',
      },
      body: buildEpsonTicketXml(pages),
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`La Epson respondió con estado ${response.status}.`);
    const xml = await response.text();
    if (!/<response\b[^>]*\bsuccess\s*=\s*["'](?:true|1)["']/iu.test(xml)) {
      const code = responseCode(xml);
      throw new Error(code ? `La Epson rechazó el ticket (${code}).` : "La Epson rechazó el ticket.");
    }
  } finally {
    window.clearTimeout(timeout);
  }
}
